import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Heading,
  Link,
  Paragraph,
  Skeleton,
  Table,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type {
  MeasureDeadline,
  ReminderAudience,
} from '../../../../api/measureDeadlines';
import {
  useMeasureDeadlinesQuery,
  useRemindAboutMeasureMutation,
} from '../../../../hooks/queries/hr/useMeasureDeadlinesQuery';
import { useUsesSafetyReps } from '../../../../hooks/queries/hms/useHmsSettingsQuery';
import { questionLanguageFor } from '../../../FormSubmission/workEnvironmentFormView';
import {
  deadlineCounts,
  deadlineTag,
  dueDateText,
  isOverdue,
  needsAttention,
  ownerStatus,
  ownerText,
  safetyRepStatus,
  safetyRepText,
} from './measureDeadlinesView';
import { surveyRoute } from '../Overview/hrOverviewView';
import { reminderStatus } from '../../../WorkEnvironmentSurveyDetail/reminderView';
import classes from './MeasureDeadlines.module.css';

const RemindButton = ({
  measure,
  to,
  label,
}: {
  measure: MeasureDeadline;
  to: ReminderAudience;
  label: string;
}): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const remind = useRemindAboutMeasureMutation();
  const { sent, failed } = reminderStatus(remind, t, i18n.language);
  if (sent)
    return (
      <Paragraph data-size="xs" role="status">
        {sent}
      </Paragraph>
    );
  if (!measure.canRemind[to]) return null;
  return (
    <>
      <Button
        variant="tertiary"
        data-size="sm"
        className={classes.inlineAction}
        loading={remind.isPending}
        aria-label={t(`hrMeasureDeadlines.remindAria.${to}`, {
          measure: measure.title,
        })}
        onClick={() => remind.mutate({ measureId: measure.id, to })}
      >
        {label}
      </Button>
      {failed && <ValidationMessage data-size="sm">{failed}</ValidationMessage>}
    </>
  );
};

/** Measures across teams past their deadline or due within two weeks, and who has been reminded */
export const MeasureDeadlinesCard = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const usesSafetyReps = useUsesSafetyReps();
  const { data, isLoading, isError } = useMeasureDeadlinesQuery(
    questionLanguageFor(i18n.language),
  );

  return (
    <section
      aria-labelledby="measure-deadlines-heading"
      className={classes.card}
    >
      <Heading level={2} data-size="xs" id="measure-deadlines-heading">
        {data
          ? t('hrMeasureDeadlines.title', deadlineCounts(data))
          : t('hrMeasureDeadlines.loadingTitle')}
      </Heading>
      {isLoading && <Skeleton variant="rectangle" width="100%" height={160} />}
      {isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('hrMeasureDeadlines.error')}</Paragraph>
        </Alert>
      )}
      {data && data.length === 0 && (
        <Paragraph data-size="sm">{t('hrMeasureDeadlines.none')}</Paragraph>
      )}
      {data && data.length > 0 && (
        <div className={classes.tableScroll}>
          <Table data-size="sm" className={classes.table}>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>
                  {t('hrMeasureDeadlines.columns.measure')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('hrMeasureDeadlines.columns.team')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('hrMeasureDeadlines.columns.deadline')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('hrMeasureDeadlines.columns.owner')}
                </Table.HeaderCell>
                {usesSafetyReps && (
                  <Table.HeaderCell>
                    {t('hrMeasureDeadlines.columns.safetyRep')}
                  </Table.HeaderCell>
                )}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {data.map((measure) => {
                const attention = needsAttention(measure);
                return (
                  <Table.Row key={measure.id}>
                    <Table.HeaderCell scope="row">
                      <span className={classes.cell}>
                        <span className="ds-label" data-size="sm">
                          {measure.title}
                        </span>
                        <span
                          className={`ds-paragraph ${classes.subtle}`}
                          data-size="xs"
                        >
                          {measure.area}
                        </span>
                      </span>
                    </Table.HeaderCell>
                    <Table.Cell>
                      <Link asChild>
                        <RouterLink to={surveyRoute(measure.surveyId)}>
                          {measure.team}
                        </RouterLink>
                      </Link>
                    </Table.Cell>
                    <Table.Cell>
                      <span className={classes.cell}>
                        <span className="ds-paragraph" data-size="sm">
                          {dueDateText(measure, i18n.language)}
                        </span>
                        <Tag
                          data-size="sm"
                          data-color={isOverdue(measure) ? 'danger' : 'warning'}
                        >
                          {deadlineTag(measure, t)}
                        </Tag>
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className={classes.cell}>
                        <span className="ds-paragraph" data-size="sm">
                          {ownerText(measure, t)}
                        </span>
                        <span
                          className={classes.status}
                          data-attention={attention.owner}
                          data-size="xs"
                        >
                          {ownerStatus(measure, t, i18n.language)}
                        </span>
                        <RemindButton
                          measure={measure}
                          to="owner"
                          label={t('hrMeasureDeadlines.remindOwner')}
                        />
                      </span>
                    </Table.Cell>
                    {usesSafetyReps && (
                      <Table.Cell>
                        <span className={classes.cell}>
                          <span className="ds-paragraph" data-size="sm">
                            {safetyRepText(measure, t)}
                          </span>
                          <span
                            className={classes.status}
                            data-attention={attention.safetyRep}
                            data-size="xs"
                          >
                            {safetyRepStatus(measure, t, i18n.language)}
                          </span>
                          <RemindButton
                            measure={measure}
                            to="safetyRep"
                            label={t('hrMeasureDeadlines.remindSafetyRep')}
                          />
                        </span>
                      </Table.Cell>
                    )}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}
      <Paragraph data-size="xs" className={classes.subtle}>
        {t(
          usesSafetyReps
            ? 'hrMeasureDeadlines.automatic'
            : 'hrMeasureDeadlines.automaticNoRep',
        )}
      </Paragraph>
    </section>
  );
};
